package kz.dentalcrm.app.screens.services.add

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.BottomSheetDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.rememberModalBottomSheetState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.Clock
import compose.icons.feathericons.FilePlus
import compose.icons.feathericons.FileText
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.service_add
import dentalcrm.composeapp.generated.resources.service_add_title
import dentalcrm.composeapp.generated.resources.service_category
import dentalcrm.composeapp.generated.resources.service_description
import dentalcrm.composeapp.generated.resources.service_duration
import dentalcrm.composeapp.generated.resources.service_naming
import dentalcrm.composeapp.generated.resources.service_price
import kz.dentalcrm.app.core.ui.UiStateScaffold
import kz.dentalcrm.app.core.ui.components.UIButton
import kz.dentalcrm.app.core.ui.components.UITextInput
import kz.dentalcrm.app.core.ui.icons.DefaultIcons
import kz.dentalcrm.app.core.ui.icons.defaulticons.Tenge
import kz.dentalcrm.app.screens.services.add.mvi.AddServiceState
import kz.dentalcrm.app.screens.services.add.mvi.AddServiceViewEvent
import kz.dentalcrm.app.screens.services.services.ServiceCategoryItem
import kz.dentalcrm.app.theme.DefaultTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun AddServiceDialog(
    onAdd: () -> Unit,
    onDismiss: () -> Unit,
) {
    val modalBottomSheetState = rememberModalBottomSheetState()
    val screenModel = koinViewModel<AddServiceViewModel>()
    val state by screenModel.viewState.collectAsState()

    if (state.screenState is AddServiceState.ScreenState.Added) {
        onAdd()
        onDismiss()
    }

    ModalBottomSheet(
        modifier = Modifier.statusBarsPadding(),
        onDismissRequest = onDismiss,
        sheetState = modalBottomSheetState,
        dragHandle = { BottomSheetDefaults.DragHandle() },
        containerColor = DefaultTheme.colors.surfaceSecondary,
        content = {
            UiStateScaffold(
                isLoading = state.screenState is AddServiceState.ScreenState.Loading,
                modifier = Modifier.background(Color.Transparent),
                content = {
                    AddServiceContent(
                        state = state,
                        event = screenModel::handleEvents
                    )
                }
            )
        }
    )
}

@Composable
private fun AddServiceContent(
    state: AddServiceState,
    event: (AddServiceViewEvent) -> Unit,
) {
    Column(
        modifier = Modifier
            .background(
                color = DefaultTheme.colors.surfaceSecondary,
            )
            .verticalScroll(rememberScrollState())
            .fillMaxWidth(),
    ) {
        var name by remember { mutableStateOf("") }
        var description by remember { mutableStateOf("") }
        var duration by remember { mutableStateOf("") }
        var price by remember { mutableStateOf("") }

        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.service_add_title),
            style = DefaultTheme.typography.bodyLargeMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
            thickness = 1.dp,
            color = DefaultTheme.colors.onSurfaceVariant.copy(
                alpha = 0.2f
            )
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))

        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.FilePlus,
            label = stringResource(Res.string.service_naming),
        ) {
            name = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .heightIn(
                    min = 120.dp
                )
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.FileText,
            singleLine = false,
            maxLines = 6,
            label = stringResource(Res.string.service_description),
        ) {
            description = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = FeatherIcons.Clock,
            keyboardType = KeyboardType.Number,
            label = stringResource(Res.string.service_duration),
        ) {
            duration = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(8.dp))
        UITextInput(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            leadingIcon = DefaultIcons.Tenge,
            keyboardType = KeyboardType.Number,
            label = stringResource(Res.string.service_price),
        ) {
            price = it
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        Text(
            modifier = Modifier.padding(horizontal = 16.dp),
            text = stringResource(Res.string.service_category),
            style = DefaultTheme.typography.bodySmallMedium,
            color = DefaultTheme.colors.onSurface
        )
        Spacer(modifier = Modifier.fillMaxWidth().height(4.dp))
        LazyRow(
            modifier = Modifier
        ) {
            item {
                Spacer(
                    modifier = Modifier.width(12.dp)
                )
            }
            items(items = state.categories, key = { it.data.id }) {
                ServiceCategoryItem(
                    item = it
                ) {
                    event(
                        AddServiceViewEvent.CategorySelected(
                            category = it.data
                        )
                    )
                }
                Spacer(
                    modifier = Modifier.width(8.dp)
                )
            }
            item {
                Spacer(
                    modifier = Modifier.width(12.dp)
                )
            }
        }

        Spacer(modifier = Modifier.fillMaxWidth().height(16.dp))
        UIButton(
            modifier = Modifier
                .padding(horizontal = 16.dp)
                .fillMaxWidth(),
            enabled = name.isNotEmpty() && description.isNotEmpty()
                    && duration.isNotEmpty() && price.isNotEmpty()
                    && state.categories.any { it.selected },
            text = stringResource(Res.string.service_add),
            isLoading = state.screenState is AddServiceState.ScreenState.Adding,
            onClick = {
                event(
                    AddServiceViewEvent.AddService(
                        naming = name,
                        description = description,
                        duration = duration,
                        price = price
                    )
                )
            },
        )

        Spacer(modifier = Modifier.fillMaxWidth().height(32.dp).navigationBarsPadding())
    }
}